//import { spawn, Thread, Worker } from "threads";

import {
  useEffect,
  useCallback,
  useContext,
  createContext,
  useMemo,
  useRef,
  useState
} from "react";
import { useGetMemItem } from "../useMemoryDB";
import useUserFields from "../useUserFields";

import { useMainContext } from "../useMainContext";
import { dataReducer, setDataStatus, mergeDataStatus } from "./dataState";
import nicetry from "nice-try";
import { createDataStore, useStoreSelector } from "./store";

import LoadingData from "./loader";
import dataWorker from "./worker";
import {
  isBatchError,
  batchConfirmMessage,
  batchSuccessMessages,
  batchErrorMessage
} from "./batch";
import {
  refusedOnMain,
  MAIN_REFUSED,
  liveConflictsOf,
  heldRowsOf
} from "./remote";
import draft from "../draft/instance";
import { heldMessage, scopeLabel } from "../draft/labels";
import { db } from "../../db";

const propertyTitle = (id) => db.peek("properties", id)?.title;

export const DataContext = createContext();

export const DataContextProvider = ({
  tenant,
  branch,
  id_token,
  username,
  children
}) => {
  const [dataLoaded, setDataLoaded] = useState(false);
  const [store] = useState(() => createDataStore(dataReducer, {}));
  const dispatch = store.dispatch;

  useEffect(() => {
    dataWorker
      .start({ tenant, branch, username, id_token, dispatch })
      .then(() => setDataLoaded(true));

    return () => {
      if (dataWorker) {
        dataWorker.stop();
      }
    };
  }, [dispatch, id_token, tenant, username, branch]);

  const settingsData = useGetMemItem("settings", "client");
  const paramsId = useGetMemItem("ui", "server")?.paramsId;
  const params = useGetMemItem("params", paramsId);

  const getSettings = useCallback(
    (keys) => {
      if (!settingsData) return;
      if (typeof keys === "function") return keys(settingsData);

      if (keys.length === 1) {
        return settingsData[keys[0]];
      } else {
        const ret = {};
        for (const key of keys) {
          ret[key] = settingsData[key];
        }

        return ret;
      }
    },
    [settingsData]
  );

  const configurations = useCallback(() => {
    dispatch(setDataStatus("loading-configurations", true));
    (async () => {
      await dataWorker.updateData(tenant, branch, id_token);
      dispatch(setDataStatus("loading-configurations", false));
    })();
  }, [dispatch, id_token, tenant, branch]);
  const volatile = useCallback(() => {
    dispatch(setDataStatus("loading-volatile", true));
    (async () => {
      await dataWorker.updateVolatileRequestMetadata(tenant, branch, id_token);
      dispatch(setDataStatus("loading-volatile", false));
    })();
  }, [dispatch, id_token, tenant, branch]);
  const metadata = useCallback(() => {
    dispatch(setDataStatus("loading-metadata", true));
    (async () => {
      await dataWorker.updateMetadata(tenant, branch);
      dispatch(setDataStatus("loading-metadata", false));
    })();
  }, [dispatch, tenant, branch]);
  const health = useCallback(() => {
    dispatch(setDataStatus("loading-health", true));
    (async () => {
      await dataWorker.updateHealthData(tenant);
      dispatch(setDataStatus("loading-health", false));
    })();
  }, [dispatch, tenant]);
  const policies = useCallback(() => {
    dispatch(setDataStatus("loading-policies", true));
    (async () => {
      await dataWorker.updatePolicies(tenant, id_token);
      dispatch(setDataStatus("loading-policies", false));
    })();
  }, [dispatch, id_token, tenant]);
  const users = useCallback(() => {
    dispatch(setDataStatus("loading-users", true));
    (async () => {
      await dataWorker.updateUsers(tenant, id_token);
      dispatch(setDataStatus("loading-users", false));
    })();
  }, [dispatch, id_token, tenant]);
  const settings = useCallback(() => {
    dispatch(setDataStatus("loading-settings", true));
    (async () => {
      await dataWorker.updateSettings(tenant, branch);
      dispatch(setDataStatus("loading-settings", false));
    })();
  }, [dispatch, tenant, branch]);
  const notes = useCallback(() => {
    dispatch(setDataStatus("loading-notes", true));
    (async () => {
      await dataWorker.updateNotes(tenant, branch);
      dispatch(setDataStatus("loading-notes", false));
    })();
  }, [dispatch, tenant, branch]);
  const deleteNote = useCallback(
    (code, index) => {
      return new Promise((resolve) => {
        dispatch(setDataStatus("deleting-note", true));
        (async () => {
          await dataWorker.deleteNote({ tenant, id_token, code, index, branch });
          dispatch(setDataStatus("deleting-note", false));
          resolve();
        })();
      });
    },
    [dispatch, id_token, tenant, branch]
  );
  const help = useCallback(() => {
    dispatch(setDataStatus("loading-help", true));
    (async () => {
      await dataWorker.updateHelp(tenant, id_token);
      dispatch(setDataStatus("loading-help", false));
    })();
  }, [dispatch, id_token, tenant]);

  useEffect(() => () => dataWorker && dataWorker.stop(), []);

  // The state is not part of the value (see useDataStateSelector): the value
  // changes only when settings/params change, not at every status update.
  const value = useMemo(
    () => ({
      store,
      dispatch,
      dataWorker,
      update: {
        configurations,
        volatile,
        metadata,
        health,
        policies,
        users,
        settings,
        notes,
        help
      },
      destroy: {
        note: deleteNote
      },
      getSettings,
      paramsId,
      params
    }),
    [
      store,
      dispatch,
      configurations,
      volatile,
      metadata,
      health,
      policies,
      users,
      settings,
      notes,
      help,
      deleteNote,
      getSettings,
      paramsId,
      params
    ]
  );

  if (!dataLoaded) return <LoadingData />;

  return <DataContext.Provider value={value}>{children}</DataContext.Provider>;
};

export const useDataContext = () => useContext(DataContext);

// Re-renders the component only when the selected value changes (compared
// shallowly), instead of at every data status update.
export const useDataStateSelector = (cb) => {
  const { store } = useDataContext();

  return useStoreSelector(store, cb);
};

export const useDataStateDispatch = () => {
  const { dispatch } = useDataContext();

  return dispatch;
};

export function useDataSetStatus() {
  const dispatch = useDataStateDispatch();

  const setStatus = useCallback(
    (k, v) => {
      if (Array.isArray(k)) {
        k.forEach(([key, value]) =>
          setTimeout(() => dispatch(setDataStatus(key, value), 0))
        );
      } else setTimeout(() => dispatch(setDataStatus(k, v)), 0);
    },
    [dispatch]
  );

  return setStatus;
}

export function useDataMergeStatus() {
  const dispatch = useDataStateDispatch();

  const mergeStatus = useCallback(
    (value) => {
      dispatch(mergeDataStatus(value));
    },
    [dispatch]
  );

  return mergeStatus;
}

export function useDataParams() {
  const { params } = useDataContext();
  return params;
}

export function useDataSettings(...keys) {
  const { getSettings } = useDataContext();
  if (typeof keys[0] === "function") return getSettings(keys[0]);
  return getSettings(keys);
}

function useDataRemote(caller, labels, options, defaultData) {
  const setStatus = useDataSetStatus();
  const { tenant, branch, id_token } = useUserFields(
    "tenant",
    "branch",
    "id_token"
  );
  const { requestConfirm, createNotification } = useMainContext();
  // labels and options are object literals created by the callers at every
  // render: reading them through refs keeps the returned function stable, as
  // it is a dependency of many callbacks (e.g. the variants tabs).
  const labelsRef = useRef(labels);
  const optionsRef = useRef(options);
  labelsRef.current = labels;
  optionsRef.current = options;

  // The save of a draft (options.draftScope) that ends without saving: the
  // scope is not saving any more (its collect made it; see draft.isSaving).
  // A save that succeeds ends with draft.clear (useDraftSave, Item).
  const settleDraft = useCallback(() => {
    const scope = optionsRef.current?.draftScope;
    if (scope) draft.saveSettled(scope);
  }, []);

  const doRemote = useCallback(
    async (dataArray, cb, cbError) => {
      const requests = [];
      const shownWarnings = new Set();
      setStatus("saving", true);

      // A request that throws before it is sent is a failed request (the
      // catch below: the draft's save settles, cbError), not an unhandled
      // rejection of doRemote that leaves the save running
      dataArray.forEach((data) => {
        data.branch = branch;
        let request;
        try {
          request = dataWorker[caller](tenant, id_token, data, (warnings) => {
            for (const warning of Array.isArray(warnings) ? warnings : []) {
              const message = typeof warning === "string" ? warning : warning?.message;
              if (!message || shownWarnings.has(message)) continue;
              shownWarnings.add(message);
              createNotification("warning", message);
            }
          });
        } catch (error) {
          request = Promise.reject(error);
        }
        requests.push(request);
      });

      Promise.all(requests)
        .then((responses) => {
          setStatus("saving", false);
          const notify = optionsRef.current?.notify;
          if (notify === true || notify === "onSuccess") {
            // A label can return several messages (a batch: one per item)
            []
              .concat(labelsRef.current.success(dataArray, responses))
              .filter(Boolean)
              .forEach((message) => createNotification("success", message));
          }
          cb && cb("OK", responses);
        })
        .catch((response) => {
          setStatus("saving", false);
          settleDraft();

          const data = isBatchError(response)
            ? response
            : nicetry(() => JSON.parse(response.message)) || {};
          const error = data.error || response;
          // A version conflict (a write with baseVersion): same message for
          // every writer; none when the caller opens the conflict dialog.
          const conflict = data.type === "conflict";

          const notify = optionsRef.current?.notify;
          if (
            (notify === true || notify === "onError") &&
            !(conflict && optionsRef.current?.conflictDialog)
          ) {
            createNotification(
              "error",
              conflict
                ? batchErrorMessage(error, data)
                : labelsRef.current.error(error, data)
            );
          }
          cbError && cbError("KO", data);
          console.log(error);
        });
    },
    [
      caller,
      createNotification,
      id_token,
      setStatus,
      settleDraft,
      tenant,
      branch
    ]
  );

  const remote = useCallback(
    (inData, cb, cbError) => {
      if (refusedOnMain(caller, branch)) {
        settleDraft();
        createNotification("error", MAIN_REFUSED);
        cbError && cbError("KO", { error: MAIN_REFUSED });
        return;
      }

      const data = inData || defaultData;
      const dataArray = Array.isArray(data) ? data : [data];

      // A row drafted in a scope this write does not save (options.draftScope:
      // the draft it saves, if any): refused, it would commit that draft
      const held = heldRowsOf(
        caller,
        dataArray,
        draft,
        optionsRef.current?.draftScope
      );
      if (held.length) {
        settleDraft();
        const [row] = held;
        createNotification(
          "warning",
          heldMessage(row, scopeLabel(row.scope, propertyTitle))
        );
        cbError && cbError("KO", { type: "held", held });
        return;
      }

      // A drafted row someone else saved or deleted meanwhile: refused here,
      // nothing is sent, as the server refuses a stale version (409).
      const conflicts = liveConflictsOf(caller, dataArray, draft);
      if (conflicts.length) {
        settleDraft();
        const refused = { type: "conflict", conflicts, local: true };
        const notify = optionsRef.current?.notify;
        if (
          (notify === true || notify === "onError") &&
          !optionsRef.current?.conflictDialog
        )
          createNotification("error", batchErrorMessage(null, refused));
        cbError && cbError("KO", refused);
        return;
      }

      if (optionsRef.current?.confirm) {
        requestConfirm(labelsRef.current.confirm(dataArray), () =>
          doRemote(dataArray, cb, cbError)
        );
      } else doRemote(dataArray, cb, cbError);
    },
    [
      branch,
      caller,
      createNotification,
      defaultData,
      doRemote,
      requestConfirm,
      settleDraft
    ]
  );

  return remote;
}

// options: { notify, confirm, conflictDialog } (conflictDialog: the caller
// shows the conflicts of a refused save, no error notification for them).
// With baseVersion in the item, a stored item with another version refuses
// the save: cbError gets { type: "conflict", conflicts }.
export function useDataSaveItem(options) {
  return useDataRemote(
    "saveDataItem",
    {
      confirm: (data) =>
        `Are you Really sure you want to Save ${data
          .map((p) => `${p.accessor} Id: ${p.Id}`)
          .join(" and ")}?`,
      success: (data) =>
        `${data.map((p) => `${p.accessor} Id: ${p.Id} Saved!`).join(" and ")}`,
      error: (error, data) => `${data.accessor} Id: ${data.Id} ${error}!`
    },
    options
  );
}

export function useDataDeleteItem(options) {
  return useDataRemote(
    "deleteDataItem",
    {
      confirm: (data) =>
        `Are you Really sure you want to Delete ${data
          .map((p) => `${p.accessor} Id: ${p.id}`)
          .join(" and ")}?`,
      success: (data) =>
        data.map((p) => `${p.accessor} Id: ${p.Id} Deleted!`).join(" and "),
      error: (error, data) => `${data.accessor} Id: ${data.Id} ${error}!`
    },
    options
  );
}

// PUT /modifiche: the server executes them (e.g. the cascade delete of the
// pages and endpoints of a property, propertiesList). remote(modifiche, cb,
// cbError): the modifiche are always passed (no pending modifiche any more:
// the operations of Properties are draft rows).
export function useDataSaveModifiche(options) {
  return useDataRemote(
    "saveModifiche",
    {
      confirm: () => `Are you Really sure you want to Save Modifiche?`,
      success: () => `Modifiche Saved!`,
      error: (error) => `Modifiche Save Error: ${error}`
    },
    options
  );
}

// One request (one commit) for the items, deletes and modifiche of an
// operation: remote({ items, deletes, modifiche }, cb, cbError). Items and
// deletes in the shape of useDataSaveItem / useDataDeleteItem; an item with
// notify: false is left out of the success notification. On a version
// conflict cbError gets { type: "conflict", conflicts }.
export function useDataSaveBatch(options) {
  return useDataRemote(
    "saveBatch",
    {
      confirm: ([batch]) => batchConfirmMessage(batch),
      success: ([batch]) => batchSuccessMessages(batch),
      error: (error, data) => batchErrorMessage(error, data)
    },
    options
  );
}

export function useDataSaveHelp(options) {
  const { update } = useDataContext();
  const lang = useGetMemItem("settings", "client")?.language;

  const func = useDataRemote(
    "helpSave",
    {
      confirm: () => `Are you Really sure you want to Save Help?`,
      success: () => `Help Saved!`,
      error: (error) => `Help Save Error: ${error}`
    },
    options
  );

  const cb = useCallback(
    (data, cb) =>
      func({ ...data, lang }, () => {
        update.help();
        cb && cb(data);
      }),
    [func, lang, update]
  );

  return cb;
}

export function useDataSaveNotes(options) {
  const { update } = useDataContext();

  const func = useDataRemote(
    "notesSave",
    {
      confirm: () => `Are you Really sure you want to Save Notes?`,
      success: () => `Notes Saved!`,
      error: (error) => `Notes Save Error: ${error}`
    },
    options
  );

  const cb = useCallback(
    (data, cb) =>
      func(data, () => {
        update.notes();
        cb && cb(data);
      }),
    [func, update]
  );

  return cb;
}

export function useDataDeleteNote(options) {
  const { destroy } = useDataContext();
  const { requestConfirm, createNotification } = useMainContext();

  const cb = useCallback(
    ({ code, index }, cb) => {
      requestConfirm(
        `Are you Really sure you want to Delete Note ${index} of ${code}?`,
        () => {
          destroy.note(code, index).then(() => {
            createNotification("success", `Note ${index} of ${code} Deleted!`);
            cb && cb();
          });
        }
      );
    },
    [createNotification, destroy, requestConfirm]
  );

  return cb;
}

export function useDataSaveUser(options) {
  const { update } = useDataContext();
  const func = useDataRemote(
    "userSave",
    {
      confirm: (data) =>
        `Are you Really sure you want to Save User ${data.nickname}`,
      success: () => `User Saved!`,
      error: (error) => `Saving User ${error}!`
    },
    options
  );

  const cb = useCallback(
    (data) => func(data, () => update.users()),
    [func, update]
  );

  return cb;
}

export function useDataDeleteUser(options) {
  const { update } = useDataContext();
  const func = useDataRemote(
    "userDelete",
    {
      confirm: (data) =>
        `Are you Really sure you want to Delete User ${data.nickname}`,
      success: () => `User Deleted!`,
      error: (error) => `Deleting User ${error}!`
    },
    options
  );

  const cb = useCallback(
    (data) => func(data, () => update.users()),
    [func, update]
  );

  return cb;
}

export function useDataSavePolicies(options) {
  return useDataRemote(
    "policiesSave",
    {
      confirm: () => `Are you Really sure you want to Save Policies?`,
      success: () => `Policies Saved!`,
      error: (error) => `Saving Policies ${error}!`
    },
    options
  );
}
